import React, { useState, useRef, useMemo } from "react";
import { Modal, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import InputField from "../../../components/form/InputField";
import CommonButton from "../../../components/commonButton";
import { fetchDropdownUsers } from "../../../redux/slice/allPropertiesSlice";
import { fetchActiveStates, fetchActiveCities } from "../../../redux/slice/locationSlice";
import withModal from "../../../components/withModal";
import CustomSelect from "../../../components/form/CustomSelect";

const ClientModalInner = ({
  onClose,
  clientData,
  isEditMode,
  isViewMode,
  isUserIdLocked,
  onSubmit,
  configData,
  isSubmitting,
}) => {
  const dispatch = useDispatch();
  const { states = [], cities = [] } = useSelector((state) => state.location || {});
  const [dropdownUsers, setDropdownUsers] = useState([]);

  const brokerSearchRef = useRef(null);
  const stateSearchRef = useRef(null);
  const citySearchRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      user_id: clientData?.user_id || "",
      name: clientData?.name || "",
      mobile_number: clientData?.mobile_number || "",
      email: clientData?.email || "",
      state_id: clientData?.state_id || "",
      city_id: clientData?.city_id || "",
      person_type: clientData?.person_type || "",
      profession: clientData?.profession || "",
      industry_sector: clientData?.industry_sector || "",
      religion_community: clientData?.religion_community || "",
      income_group: clientData?.income_group || "",
      age: clientData?.age || "",
      family_size: clientData?.family_size || "",
      is_associate: clientData?.is_associate || false,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      user_id: Yup.string().required("User is required"),
      name: Yup.string().required("Name is required"),
      mobile_number: Yup.string().required("Mobile number is required"),
      email: Yup.string().email("Invalid email address"),
    }),
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const brokerOptions = useMemo(() => {
    const fetched = dropdownUsers.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name} (${u.mobile_number || '-'})` }));
    if (clientData?.user && !fetched.find(o => o.value === clientData.user_id)) {
      fetched.unshift({ value: clientData.user_id, label: `${clientData.user.first_name} ${clientData.user.last_name} (${clientData.user.mobile_number || '-'})` });
    }
    return fetched;
  }, [dropdownUsers, clientData]);

  const stateOptions = useMemo(() => {
    const fetched = states.map(s => ({ value: s.id, label: s.name }));
    if (clientData?.state && !fetched.find(o => o.value === clientData.state_id)) {
      fetched.unshift({ value: clientData.state_id, label: clientData.state.name });
    }
    return fetched;
  }, [states, clientData]);

  const cityOptions = useMemo(() => {
    const fetched = cities.map(c => ({ value: c.id, label: c.name }));
    if (clientData?.city && !fetched.find(o => o.value === clientData.city_id)) {
      fetched.unshift({ value: clientData.city_id, label: clientData.city.name });
    }
    return fetched;
  }, [cities, clientData]);

  const handleBrokerSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (brokerSearchRef.current) clearTimeout(brokerSearchRef.current);
      brokerSearchRef.current = setTimeout(() => {
        dispatch(fetchDropdownUsers({ search: inputValue, limit: 50 })).unwrap()
          .then(res => setDropdownUsers(res?.data?.data || res?.data || []))
          .catch(() => { });
      }, 400);
    }
  };

  const handleStateSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (stateSearchRef.current) clearTimeout(stateSearchRef.current);
      stateSearchRef.current = setTimeout(() => dispatch(fetchActiveStates({ search: inputValue, limit: 50 })), 400);
    }
  };

  const handleCitySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (citySearchRef.current) clearTimeout(citySearchRef.current);
      citySearchRef.current = setTimeout(() => {
        if (formik.values.state_id)
          dispatch(fetchActiveCities({ state_ids: formik.values.state_id, search: inputValue, limit: 50 }));
      }, 400);
    }
  };

  const convertToOptions = (arr) => arr?.map((item) => ({ label: item, value: item })) || [];


  return (
    <Form onSubmit={formik.handleSubmit}>
      <Modal.Body className="grid">

        <CustomSelect
          inputId="user_id"
          className="g-col-6"
          required
          options={brokerOptions}
          value={brokerOptions.find(o => o.value === formik.values.user_id) || null}
          onChange={(opt) => formik.setFieldValue("user_id", opt?.value || "")}
          onInputChange={handleBrokerSearch}
          onMenuOpen={() => {
            dispatch(fetchDropdownUsers({ limit: 50 })).unwrap()
              .then(res => setDropdownUsers(res?.data?.data || res?.data || []))
              .catch(() => { });
          }}
          onBlur={formik.handleBlur}
          placeholder="Select Broker"
          isClearable
          isDisabled={isEditMode || isViewMode || isUserIdLocked}
          label={{ name: "Broker (User)" }}
          filterOption={() => true}
          validation={{
            isInvalid: formik.touched.user_id && !!formik.errors.user_id,
            message: formik.errors.user_id,
          }}
        />


        <InputField
          className="g-col-6"
          label={{ name: "Name" }}
          required
          input={{
            name: "name",
            value: formik.values.name,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: "Enter client name",
            disabled: isViewMode,
          }}
          validation={{
            isInvalid: formik.touched.name && !!formik.errors.name,
            message: formik.errors.name,
          }}
        />

        <InputField
          className="g-col-6"
          label={{ name: "Mobile Number" }}
          required
          input={{
            name: "mobile_number",
            value: formik.values.mobile_number,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: "Enter mobile number",
            disabled: isViewMode,
          }}
          validation={{
            isInvalid: formik.touched.mobile_number && !!formik.errors.mobile_number,
            message: formik.errors.mobile_number,
          }}
        />

        <InputField
          className="g-col-6"
          label={{ name: "Email" }}
          input={{
            name: "email",
            type: "email",
            value: formik.values.email,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: "Enter email",
            disabled: isViewMode,
          }}
          validation={{
            isInvalid: formik.touched.email && !!formik.errors.email,
            message: formik.errors.email,
          }}
        />



        <CustomSelect
          className="g-col-6"
          label={{
            name: "State"
          }}
          inputId="state_id"
          options={stateOptions}
          value={stateOptions.find(o => o.value === formik.values.state_id) || null}
          onChange={(opt) => {
            formik.setFieldValue("state_id", opt?.value || "");
            formik.setFieldValue("city_id", "");
          }}
          onInputChange={handleStateSearch}
          onMenuOpen={() => dispatch(fetchActiveStates({ limit: 50 }))}
          onBlur={formik.handleBlur}
          placeholder="Select State"
          isClearable
          isDisabled={isViewMode}
          filterOption={() => true}
        />
        <CustomSelect
          className="g-col-6"
          label={{ name: "City" }}
          inputId="city_id"
          options={cityOptions}
          value={cityOptions.find(o => o.value === formik.values.city_id) || null}
          onChange={(opt) => formik.setFieldValue("city_id", opt?.value || "")}
          onInputChange={handleCitySearch}
          onMenuOpen={() => {
            if (formik.values.state_id) {
              dispatch(fetchActiveCities({ state_ids: formik.values.state_id, limit: 50 }));
            }
          }}
          onBlur={formik.handleBlur}
          placeholder="Select City"
          isClearable
          isDisabled={isViewMode || !formik.values.state_id}
          filterOption={() => true}
        />

        <CustomSelect
          className="g-col-6"
          label={{ name: "Person Type" }}
          options={convertToOptions(configData?.person_type)}
          value={convertToOptions(configData?.person_type).find((c) => c.value === formik.values.person_type) || null}
          onChange={(option) => formik.setFieldValue("person_type", option?.value || "")}
          onBlur={formik.handleBlur}
          placeholder="Select Person Type"
          isClearable
          isDisabled={isViewMode}
        />

        <CustomSelect
          className="g-col-6"
          label={{ name: "Profession" }}
          inputId="profession"
          options={convertToOptions(configData?.profession)}
          value={convertToOptions(configData?.profession).find((c) => c.value === formik.values.profession) || null}
          onChange={(option) => formik.setFieldValue("profession", option?.value || "")}
          onBlur={formik.handleBlur}
          placeholder="Select Profession"
          isClearable
          isDisabled={isViewMode}
        />


        <CustomSelect
          className="g-col-6"
          label={{ name: "Industry Sector" }}
          options={convertToOptions(configData?.industry_sector)}
          value={convertToOptions(configData?.industry_sector).find((c) => c.value === formik.values.industry_sector) || null}
          onChange={(option) => formik.setFieldValue("industry_sector", option?.value || "")}
          onBlur={formik.handleBlur}
          placeholder="Select Industry Sector"
          isClearable
          isDisabled={isViewMode}
        />



        <CustomSelect
          className="g-col-6"
          label={{ name: "Religion/Community" }}
          options={convertToOptions(configData?.religion_community)}
          value={convertToOptions(configData?.religion_community).find((c) => c.value === formik.values.religion_community) || null}
          onChange={(option) => formik.setFieldValue("religion_community", option?.value || "")}
          onBlur={formik.handleBlur}
          placeholder="Select Religion/Community"
          isClearable
          isDisabled={isViewMode}

        />



        <CustomSelect
          className="g-col-6"
          label={{ name: "Income Group" }}
          options={convertToOptions(configData?.income_group)}
          value={convertToOptions(configData?.income_group).find((c) => c.value === formik.values.income_group) || null}
          onChange={(option) => formik.setFieldValue("income_group", option?.value || "")}
          onBlur={formik.handleBlur}
          placeholder="Select Income Group"
          isClearable
          isDisabled={isViewMode}
        />


        <InputField
          className="g-col-3"
          label={{ name: "Age" }}
          input={{
            name: "age",
            type: "number",
            value: formik.values.age,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: "Age",
            disabled: isViewMode,
          }}
        />

        <InputField
          className="g-col-3"
          label={{ name: "Family Size" }}
          input={{
            name: "family_size",
            type: "number",
            value: formik.values.family_size,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: "Size",
            disabled: isViewMode,
          }}
        />

        <Form.Group className="g-col-12 d-flex align-items-center">
          <Form.Check
            type="checkbox"
            id="is_associate"
            name="is_associate"
            label="Is Associate?"
            checked={formik.values.is_associate}
            onChange={formik.handleChange}
            className="mt-2"
            disabled={isViewMode}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {isViewMode ? (
          <CommonButton
            title="Close"
            variant="outline"
            type="button"
            onClick={onClose}
          />
        ) : (
          <CommonButton
            title={isSubmitting ? "Saving..." : isEditMode ? "Update Client" : "Save Client"}
            variant="primary"
            type="submit"
            disabled={isSubmitting}
          />
        )}
      </Modal.Footer>
    </Form>
  );
};

const ClientModal = withModal(ClientModalInner);

export default ClientModal;
